import { FC, useEffect, useState } from "react";
import { Form } from "react-bootstrap";

interface Props {
  defaultValue: number;
  onCommit: (value: number) => void;
}

/**
 * The step's position in its pricing group, typed rather than dragged.
 *
 * Sequence is display order and nothing else (MPN-1589). It used to gate
 * billing — an item at 0 was read as a funnel-entry marker and never
 * evaluated — and the drag-to-reorder UI reindexed from 0 on every drop, so
 * the first line of every pricing group was silently unbillable.
 */
const SequenceInput: FC<Props> = ({ defaultValue, onCommit }) => {
  const [value, setValue] = useState(String(defaultValue));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setValue(String(defaultValue));
  }, [defaultValue]);

  const commit = () => {
    setEditing(false);
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0) {
      setValue(String(defaultValue));
      return;
    }
    if (parsed !== defaultValue) onCommit(parsed);
  };

  return (
    <Form.Control
      type="number"
      min={0}
      step={1}
      size="sm"
      aria-label="Step order"
      value={value}
      onFocus={() => setEditing(true)}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      style={{ fontFamily: "monospace", textAlign: "center", padding: "2px 4px" }}
    />
  );
};

export default SequenceInput;
